import { useState } from "react";
import { ArrowRight, Sparkles, Lock, Globe } from "lucide-react";

export function SplashScreens({ onFinish }) {
  const [currentStep, setCurrentStep] = useState(0);

  const slides = [
    {
      title: "Seamlessly. Accessible. Everywhere.",
      description:
        "Designed for those who demand absolute precision in every global transaction and liquidity move.",
      badge: "Chat it, move it, track it",
      icon: Sparkles,
    },
    {
      title: "Uncompromising Security",
      description:
        "Encrypted at rest and in motion. Your financial ecosystem remains private, secure, and resilient.",
      badge: "No secrets spilled",
      icon: Lock,
    },
    {
      title: "Borderlessly Fluid",
      description:
        "Move capital across continents with zero friction, instant settlements, and complete transparency.",
      badge: "Global Liquidity",
      icon: Globe,
    },
  ];

  const handleNext = () => {
    if (currentStep < slides.length - 1) {
      setCurrentStep((step) => step + 1);
    } else {
      onFinish?.();
    }
  };

  const slide = slides[currentStep];
  const IconComponent = slide.icon;

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#FDFDFD] text-[#0A4730]">
      <div className="mx-auto flex min-h-screen w-full max-w-[1440px] flex-col px-4 py-5 sm:px-6 sm:py-6 md:px-10 lg:px-16 xl:px-20">

        {/* Header */}
        <header className="flex shrink-0 items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-[#0A4730] text-white shadow-[0_7px_20px_rgba(10,71,48,0.12)] sm:h-10 sm:w-10 sm:rounded-[14px]">
              <span className="font-serif text-base font-bold sm:text-lg">
                B
              </span>
            </div>

            <div className="flex min-w-0 flex-col">
              <span className="font-serif text-[17px] font-bold leading-none tracking-tight sm:text-lg">
                Blupay
              </span>

              <span className="mt-1 text-[7px] font-semibold uppercase tracking-[0.28em] text-[#0A4730]/50 sm:text-[8px]">
                Chat Pay
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3 sm:gap-5">
            {/* Progress — visible on mobile too */}
            <div className="flex items-center gap-1.5">
              {slides.map((_, index) => (
                <div
                  key={index}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    index === currentStep
                      ? "w-7 bg-[#0A4730]"
                      : "w-1.5 bg-slate-200"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={onFinish}
              className="shrink-0 px-1 py-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-400 transition-colors hover:text-[#0A4730] sm:px-2 sm:text-[10px]"
            >
              Skip
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex w-full flex-1 flex-col items-center justify-center py-7 sm:py-10 md:py-12 lg:py-16">

          <div className="w-full min-w-0 max-w-[800px]">

            {/* Card */}
            <div className="relative w-full overflow-hidden rounded-[26px] border border-slate-100 bg-white px-5 py-7 shadow-[0_20px_60px_-30px_rgba(10,71,48,0.14)] sm:rounded-[32px] sm:px-10 sm:py-10 md:px-14 md:py-12 lg:rounded-[36px] lg:px-20 lg:py-14">

              {/* Ambient glow */}
              <div className="pointer-events-none absolute left-1/2 top-0 h-52 w-52 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0A4730]/[0.045] blur-3xl sm:h-64 sm:w-64" />

              {/* Illustration */}
              <div className="relative mx-auto mb-6 flex h-[108px] w-[108px] items-center justify-center sm:mb-8 sm:h-32 sm:w-32 md:h-36 md:w-36">

                <div className="absolute inset-0 rounded-full border border-[#0A4730]/[0.08]" />

                <div className="absolute inset-2.5 rounded-full border border-[#0A4730]/[0.045]" />

                <div className="absolute left-0 top-4 h-[72px] w-[58px] rounded-full bg-[#0A4730]/[0.08] sm:h-24 sm:w-20" />

                <div className="absolute right-0 top-3 flex h-[78px] w-[58px] items-center justify-center rounded-full bg-[#0A4730] shadow-[0_12px_30px_rgba(10,71,48,0.18)] sm:h-28 sm:w-20">
                  <IconComponent
                    key={currentStep}
                    size={27}
                    strokeWidth={1.4}
                    className="text-white sm:h-8 sm:w-8"
                  />
                </div>
              </div>

              {/* Text */}
              <div
                key={currentStep}
                className="relative mx-auto w-full max-w-[560px] text-center animate-in fade-in slide-in-from-bottom-2 duration-500"
              >
                <span className="inline-flex max-w-full rounded-full bg-[#0A4730]/[0.055] px-3 py-1.5 text-[8px] font-bold uppercase tracking-[0.2em] text-[#0A4730] sm:px-4 sm:text-[9px] sm:tracking-[0.25em]">
                  {slide.badge}
                </span>

                <h1 className="mt-4 break-words font-serif text-[24px] font-semibold leading-[1.12] tracking-[-0.025em] text-[#0A4730] sm:mt-5 sm:text-[31px] md:text-[36px] lg:text-[42px]">
                  {slide.title}
                </h1>

                <p className="mx-auto mt-3 max-w-[500px] text-[13px] font-light leading-[1.65] text-slate-500 sm:mt-4 sm:text-[14px] md:text-base md:leading-7">
                  {slide.description}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-5 flex justify-center sm:mt-6 md:justify-end">
              <button
                onClick={handleNext}
                className="group flex h-11 min-w-[136px] items-center justify-center gap-2.5 rounded-xl bg-[#0A4730] px-5 text-[9px] font-bold uppercase tracking-[0.17em] text-white shadow-[0_8px_22px_rgba(10,71,48,0.13)] transition-all duration-200 hover:bg-[#083b28] hover:shadow-[0_10px_28px_rgba(10,71,48,0.17)] active:scale-[0.98] sm:h-12 sm:min-w-[150px] sm:text-[10px]"
              >
                <span>
                  {currentStep === slides.length - 1 ? "Enter" : "Continue"}
                </span>

                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                />
              </button>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}